import axios, { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig, AxiosHeaders, AxiosInstance } from "axios";
import {
   
    LOGIN_SUCCESS,
    SET_XTOTAL_TEUDOT_DETAILS,
} from "../../../store/actions/actionsTypes";
import store from "../../../store/store";
import {
    STATIC_MONGO,
    STATIC_PATH,
    STATIC_PATH_V2,
    STATIC_PATH_V2_TEST,
    STATIC_PATH_WSPALLETS,
} from "./settings";
import { resetState } from "../../../store/actions/resetState";

type TReqestsInstanceVariables = "go" | "mongo" | "node" | "wspallets";

const documentLang = document.documentElement.lang;
const langFromCookie = document.cookie.split(';').find((item) => item.includes('i18next'))?.split('=')[1];

// console.log('langFromCookie', langFromCookie)

const getToken = () => {
    const token = localStorage.getItem("user");
    if (!token) return;
    return JSON.parse(token);
};

const staticInstance = {
    timeout: 120000,
    headers: {
        Authorization: "Bearer " + getToken(),
        "Accept-Language": langFromCookie || documentLang,
    },
};

// This code exports an object called `instance`, which is an object containing three different Axios instances,
// one for each of the specified routes: `mongo`, `go`, and `node`. These Axios instances are used to make HTTP requests
// to the corresponding routes.
export const instance = {
    mongo: axios.create({
        baseURL: STATIC_MONGO,
        ...staticInstance,
    }),
    go: axios.create({
        ...staticInstance,
        // PROD
        baseURL: STATIC_PATH_V2,
        // TEST
        // baseURL: STATIC_PATH_V2_TEST
        // baseURL: "http://192.168.201.57:8087"
    }),
    node: axios.create({
        ...staticInstance,
        baseURL: STATIC_PATH,
    }),
    wspallets: axios.create({
        ...staticInstance,
        baseURL: STATIC_PATH_WSPALLETS,
    }),
    // The `instance` object also has a getter called `state` that returns an object containing the three Axios instances.
    get state() {
        return {
            mongo: this.mongo,
            go: this.go,
            node: this.node,
            wspallets: this.wspallets,
        };
    },
    // It also has a setter called `updateToken` which takes a string `token` as its argument.
    // This setter updates the `Authorization` header for each of the Axios instances with the provided `token`.
    set updateToken(token: string) {
        this.mongo.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        this.go.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        this.node.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        this.wspallets.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
};

// Add a request interceptor to all instances to ensure fresh token on every request
const addRequestInterceptor = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Get fresh token on each request
      const freshToken = getToken();
      if (freshToken) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${freshToken}`;
      }
      return config;
    },
    (error: any) => {
      return Promise.reject(error);
    }
  );
};

// Apply interceptor to all instances
addRequestInterceptor(instance.mongo);
addRequestInterceptor(instance.go);
addRequestInterceptor(instance.node);
addRequestInterceptor(instance.wspallets);

// The code sets up response interceptors for the `go` and `node` Axios instances to catch any errors that may
// occur during the request. If the error message is `jwt malformed`, `jwt expired`, or the status is
instance.go.interceptors.response.use(
    (response: AxiosResponse) => {
        store.dispatch({
            type: SET_XTOTAL_TEUDOT_DETAILS,
            payload: response.headers["x-total-count"],
        });
        return response;
    },
    (err: any) => {
        if (
            err.response.status === 401
            // err.response?.data.message  "jwt expired" ||
        ) {
            return retryRequest(err.config); // Call
        }
        return Promise.reject(err);
    }
);

instance.node.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    (err: any) => {
        if (
            err.response?.data.message === "jwt expired" ||
            err.response?.data.message === "jwt malformed"
        ) {
            return retryRequest(err.config); // Call
        }
        return Promise.reject(err);
    }
);
instance.wspallets.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    (err: any) => {
        if (
            err.response?.data.message === "jwt expired" ||
            err.response?.data.message === "jwt malformed" || 
            err?.response?.status === 401
        ) {
            return retryRequest(err.config); // Call
        }
        return Promise.reject(err);
    }
);

export const refreshAccessToken = async (refreshToken: string) => {
    try {
        const response = await axios.post(STATIC_PATH_V2 + "/auth/refresh_token", {
            RefreshToken: refreshToken,
        });
        localStorage.setItem('user', JSON.stringify(response.data.token));
        store.dispatch({ type: LOGIN_SUCCESS, payload: response.data });
        return response.data.token;

    } catch (error) {
        // Handle refresh token error, e.g., log the user out.
        throw error;
    }
};

const retryRequest = async (originalRequest: InternalAxiosRequestConfig) => {
    try {
        const refreshToken = store.getState().mainReducer.refresh_token;
        const accessToken = await refreshAccessToken(refreshToken as string);
       
        // Update auth token in all instance headers
        instance.mongo.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        instance.go.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        instance.node.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        instance.wspallets.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        
        
        // Update the original request headers
        if (originalRequest.headers) {
            originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        
        // Retry the original request
        return axios(originalRequest)
    } catch (error: any) {
        if (error.response && error.response.status === 403) {
            store.dispatch(resetState())    
        
        }
        throw error;
    }
};

const responseBody = (response: AxiosResponse) => response.data;
// The code also exports an object called `requests`, which has functions for making different types of HTTP requests
// (`get`, `post`, `put`, `patch`, and `delete`) to the specified route using the corresponding Axios instance from
// the `instance` object. The `responseBody` function is used to extract the data from the response object returned
// by the Axios instance.
export const requests = {
    get: (url: string, route: TReqestsInstanceVariables, config?: AxiosRequestConfig) =>
        instance[route].get(url, config).then(responseBody),
    post: (url: string, route: TReqestsInstanceVariables, body: any, config?: AxiosRequestConfig) =>
        instance[route].post(url, body, config).then(responseBody),
    put: (url: string, route: TReqestsInstanceVariables, body: any, config?: AxiosRequestConfig) =>
        instance[route].put(url, body, config).then(responseBody),
    patch: (url: string, route: TReqestsInstanceVariables, body: any, config?: AxiosRequestConfig) =>
        instance[route].patch(url, body, config).then(responseBody),
    delete: (url: string, route: TReqestsInstanceVariables, config?: AxiosRequestConfig) =>
        instance[route].delete(url, config).then(responseBody),
};
